import { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
} from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import envConfig from "../../config";
import { getHeaders } from "../../utils/api";
import AuthExaccLayout from "../../components/AuthExaccLayout/AuthExaccLayout";
import ThemeToggle from "../../components/ThemeToggle/ThemeToggle";
import { useTheme } from "../../context/ThemeContext";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { ReactComponent as GoogleAuthIcon } from "./google_authenticator.svg";
import { ReactComponent as MicrosoftAuthIcon } from "./microsoft_authenticator.svg";
import { ReactComponent as GooglePlayBadge } from "./google_play_badge_en.svg";
import { ReactComponent as AppStoreBadge } from "./app_store_badge_blk.svg";

/** Allowed GlobalUserStatus values from generate-2fa-security-settings for /mfa-setup access. */
const MFA_SETUP_ALLOWED_GLOBAL_USER_STATUS = new Set(["A", "D"]);

const TabPanel = ({ children, value, index }) => {
  return (
    <Box hidden={value !== index} sx={{ mt: 3 }}>
      {value === index && children}
    </Box>
  );
};

const TwoFactorSetup = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [manualEntryKey, setManualEntryKey] = useState("");
  const [hashKey, setHashKey] = useState("");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [hasEnabled2FA, setHasEnabled2FA] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetch2FASettings = async () => {
      const url = `https://${envConfig.apiDev1}/api/v3/en-au/user2fa/generate-2fa-security-settings?Size=160`;

      try {
        const response = await fetch(url, {
          headers: getHeaders(),
          method: "GET",
        });
        const data = await response.json();

        if (response.ok) {
          const raw = data.GlobalUserStatus;
          const status =
            typeof raw === "string" ? raw.trim().toUpperCase() : "";
          if (status && !MFA_SETUP_ALLOWED_GLOBAL_USER_STATUS.has(status)) {
            enqueueSnackbar(
              "2FA setup is not available for your account status. Redirecting to the dashboard.",
              { variant: "warning" }
            );
            navigate("/user-dashboard", { replace: true });
            return;
          }
          setQrCodeUrl(data.QrCodeSetupImageUrl);
          setManualEntryKey(data.ManualEntryKey);
          setHashKey(data.Secret2FAKey);
          setHasEnabled2FA(data.CurrentUser2FAHasEnabledYN === "Y");
        } else {
          // Error handled by interceptor
        }
      } catch (error) {
        // Error handled by interceptor
      }
    };

    fetch2FASettings();
  }, [navigate]);

  const handleEnable2FA = async () => {
    if (!twoFactorCode || !twoFactorCode.trim()) {
      enqueueSnackbar("Please enter a 2FA code before proceeding.", {
        variant: "error",
      });
      return;
    }

    if (loading) return;

    setLoading(true);
    const url = `https://${envConfig.apiDev1}/api/v3/en-au/user2fa/enable-2fa-security`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          Secret2FAKey: hashKey,
          User2FACode: twoFactorCode,
        }),
      });
      const data = await response.json();

      if (response.ok && data.User2FAEnabledYN === "Y") {
        const { markAuthenticationComplete } = require("../../utils/auth");
        markAuthenticationComplete();

        enqueueSnackbar("2FA has been enabled successfully.", {
          variant: "success",
        });

        await new Promise((resolve) => setTimeout(resolve, 500));

        navigate("/user-dashboard");
      } else {
        enqueueSnackbar(
          "Code incorrect, try again. Please wait for new code to appear on your app.",
          { variant: "error" }
        );
        setLoading(false);
      }
    } catch (error) {
      enqueueSnackbar(
        "Code incorrect, try again. Please wait for new code to appear on your app.",
        { variant: "error" }
      );
      setLoading(false);
    }
  };

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    enqueueSnackbar("Hash code copied to clipboard", { variant: "success" });
  };

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Box className="glass-card" sx={{ p: { xs: 3.5, md: 4 }, width: "100%" }}>
      <Typography variant="h2" sx={{ fontWeight: 700, mb: 1, color: "text.primary" }}>
        Set up your 2FA
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Scan the QR code with your authenticator app, then enter the generated code to confirm.
      </Typography>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 1,
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: "13px",
            minHeight: "48px",
            display: "flex",
            flexDirection: "row",
            gap: 0.75,
            color: "text.secondary",
          },
          "& .Mui-selected": {
            color: "primary.main",
          },
          "& .MuiTabs-indicator": {
            backgroundColor: "primary.main",
          },
        }}
      >
        <Tab
          icon={<CheckIcon sx={{ fontSize: 18 }} />}
          label="I have authenticator app"
          iconPosition="start"
        />
        <Tab
          icon={<CloseIcon sx={{ fontSize: 18 }} />}
          label="I don't have authenticator app"
          iconPosition="start"
        />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <Box>
          <Typography variant="h6" sx={{ color: "text.primary", mb: 2, fontWeight: 600 }}>
            1. Scan code
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Scan this code on your authenticator app:
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            To do that click the icon of hash code on the bottom-right corner on the app.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 4,
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            {qrCodeUrl && (
              <Box
                sx={{
                  flexShrink: 0,
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <img
                  src={qrCodeUrl}
                  alt="QR Code"
                  style={{
                    width: "180px",
                    height: "180px",
                    display: "block",
                  }}
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: "220px" }}>
              <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                Can't scan? Copy the below hash to the app
              </Typography>
              <TextField
                fullWidth
                label="Hash Code"
                value={manualEntryKey}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyToClipboard text={manualEntryKey} onCopy={handleCopy}>
                        <Button
                          variant="outlined"
                          className="btn-glass"
                          sx={{
                            textTransform: "none",
                            mr: -1.5,
                            px: 1.5,
                            py: 0.75,
                            minWidth: 0,
                            fontSize: "13px",
                          }}
                          startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
                          data-testid="mfaconfig--Button-0"
                        >
                          {copied ? "Copied!" : "Copy Hash"}
                        </Button>
                      </CopyToClipboard>
                    </InputAdornment>
                  ),
                }}
                data-testid="mfaconfig--TextField-0"
              />
            </Box>
          </Box>

          <Divider sx={{ my: 3, borderColor: "divider" }} />

          <Typography variant="h6" sx={{ color: "text.primary", mb: 2, fontWeight: 600 }}>
            2. Enter code
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
            Enter the generated code to confirm successful setup
          </Typography>

          <TextField
            fullWidth
            label="2FA Code"
            placeholder="XXXXXXXX"
            value={twoFactorCode}
            onChange={(e) => setTwoFactorCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleEnable2FA();
              }
            }}
            sx={{ mb: 3 }}
            data-testid="mfaconfig--TextField-1"
          />

          <Button
            variant="outlined"
            className="btn-glass"
            fullWidth
            onClick={handleEnable2FA}
            disabled={!twoFactorCode || !twoFactorCode.trim() || loading}
            startIcon={<CheckIcon />}
            data-testid="mfaconfig--Button-1"
          >
            Save & Next
          </Button>

          {hasEnabled2FA && (
            <Alert severity="warning" sx={{ mt: 2 }} data-testid="mfaconfig--Alert-0">
              Your user has the active 2FA security enabled already. By saving, you will
              overwrite existing 2FA setup and old 2FA will stop working.
            </Alert>
          )}
        </Box>
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: "text.primary", fontWeight: 600 }}>
            Get authenticator app
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, color: "text.secondary" }}>
            Download one of these authenticator apps to your phone.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
              mb: 4,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ mx: "auto", mb: 2 }}>
                <MicrosoftAuthIcon width="80" height="80" />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600 }}>
                Microsoft Authenticator
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <a
                  href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Microsoft Authenticator on Google Play"
                  style={{ textDecoration: "none" }}
                  data-testid="mfaconfig--a-0"
                >
                  <Button
                    variant="outlined"
                    className="btn-glass"
                    fullWidth
                    sx={{ textTransform: "none", py: 1.25 }}
                    data-testid="mfaconfig--Button-2"
                  >
                    <GooglePlayBadge
                      aria-hidden
                      style={{ height: 24, width: "auto", display: "block" }}
                    />
                  </Button>
                </a>
                <a
                  href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Microsoft Authenticator on the App Store"
                  style={{ textDecoration: "none" }}
                  data-testid="mfaconfig--a-1"
                >
                  <Button
                    variant="outlined"
                    className="btn-glass"
                    fullWidth
                    sx={{ textTransform: "none", py: 1.25 }}
                    data-testid="mfaconfig--Button-3"
                  >
                    <AppStoreBadge
                      aria-hidden
                      style={{ height: 24, width: "auto", display: "block" }}
                    />
                  </Button>
                </a>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Box sx={{ mx: "auto", mb: 2 }}>
                <GoogleAuthIcon width="80" height="80" />
              </Box>
              <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 600 }}>
                Google Authenticator
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <a
                  href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get Google Authenticator on Google Play"
                  style={{ textDecoration: "none" }}
                  data-testid="mfaconfig--a-2"
                >
                  <Button
                    variant="outlined"
                    className="btn-glass"
                    fullWidth
                    sx={{ textTransform: "none", py: 1.25 }}
                    data-testid="mfaconfig--Button-4"
                  >
                    <GooglePlayBadge
                      aria-hidden
                      style={{ height: 24, width: "auto", display: "block" }}
                    />
                  </Button>
                </a>
                <a
                  href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download Google Authenticator on the App Store"
                  style={{ textDecoration: "none" }}
                  data-testid="mfaconfig--a-3"
                >
                  <Button
                    variant="outlined"
                    className="btn-glass"
                    fullWidth
                    sx={{ textTransform: "none", py: 1.25 }}
                    data-testid="mfaconfig--Button-5"
                  >
                    <AppStoreBadge
                      aria-hidden
                      style={{ height: 24, width: "auto", display: "block" }}
                    />
                  </Button>
                </a>
              </Box>
            </Box>
          </Box>

          <Button
            variant="outlined"
            className="btn-glass"
            fullWidth
            onClick={() => setActiveTab(0)}
            data-testid="mfaconfig--Button-6"
          >
            Return To Setting 2FA
          </Button>
        </Box>
      </TabPanel>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <ThemeToggle
          titlefontSize={16}
          title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
        />
      </Box>
    </Box>
  );
};

export default function MFAConfigPage() {
  return (
    <AuthExaccLayout contentMaxWidth={560} stickyLeftVisual>
      <TwoFactorSetup />
    </AuthExaccLayout>
  );
}
